import { Button } from '@cibli/shared';
import { CheckCircle, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

const ATS_API_URL = import.meta.env.VITE_ATS_API_URL || 'http://localhost:3010';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx';
const MAX_FILE_SIZE_MB = 10;

export default function CVUploadPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    (f: File) => {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        setError(t('apply.upload.invalidType', 'Format non accepté. Utilisez PDF, DOC ou DOCX.'));
        return;
      }
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(
          t('apply.upload.tooLarge', 'Fichier trop volumineux. Maximum {{maxSize}} Mo.', {
            maxSize: MAX_FILE_SIZE_MB,
          }),
        );
        return;
      }
      setError(null);
      setFile(f);
    },
    [t],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (jobId) formData.append('applicationId', jobId);

      const response = await fetch(`${ATS_API_URL}/CV/create`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          message?: string;
          error?: string;
        };
        throw new Error(
          data.message ?? data.error ?? t('apply.upload.error', "Erreur lors de l'envoi"),
        );
      }

      setUploaded(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('apply.upload.error', "Erreur lors de l'envoi"),
      );
    } finally {
      setIsUploading(false);
    }
  };

  if (uploaded) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <CheckCircle className="size-14 text-green-500 mx-auto" />
          <h2 className="text-xl font-semibold">{t('apply.upload.success', 'CV envoyé !')}</h2>
          <p className="text-muted-foreground">
            {t('apply.upload.successDesc', 'Votre candidature a bien été reçue.')}
          </p>
          <Button onClick={() => navigate('/')}>
            {t('apply.upload.done', "Retour à l'accueil")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">{t('apply.upload.title', 'Déposer votre CV')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('apply.upload.subtitle', 'Formats acceptés : PDF, DOC, DOCX')}
          </p>
        </div>

        <div
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`
            relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed
            p-10 cursor-pointer transition-colors
            ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50 hover:bg-accent'}
          `}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            className="hidden"
            onChange={onInputChange}
          />
          {file ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate max-w-xs">{file.name}</span>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </p>
            </>
          ) : (
            <>
              <Upload className="size-8 text-muted-foreground" />
              <p className="text-sm text-center text-muted-foreground">
                {t('apply.upload.dropHint', 'Glissez votre fichier ici ou cliquez pour parcourir')}
              </p>
            </>
          )}
        </div>

        {error && <p className="text-sm text-destructive text-center">{error}</p>}

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(`/apply/${jobId}`)} className="flex-1">
            {t('common.back', 'Retour')}
          </Button>
          <Button onClick={handleSubmit} disabled={!file || isUploading} className="flex-1">
            {isUploading
              ? t('apply.upload.uploading', 'Envoi en cours...')
              : t('apply.upload.submit', 'Envoyer mon CV')}
          </Button>
        </div>
      </div>
    </div>
  );
}
